import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import s from "./styles.module.scss";
import Button from "../../components/button";
import Modal from "../../components/modal";
import { useClerkAuth } from "../../hooks/use-clerk-auth";
import apiClient from "../../services/api-client";
import PageHeader from "../../components/page-header";
import Pagination from "../../components/pagination";
import CourseCard from "../../components/course-card";
import TaskItem from "../../components/task-item";
import { Loading } from "../loading";
import config from "../../config";

// Module-scope so they're stable references shared across renders
const TASKS_CACHE_TODAY_KEY = 'ocean_tasks_today';
const TASKS_CACHE_UPCOMING_KEY = 'ocean_tasks_upcoming';

const Courses = () => {
  const { user, initDone, getSessionToken } = useClerkAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSections, setSelectedSections] = useState({});
  const [courses, setCourses] = useState([]);
  const [originalCourses, setOriginalCourses] = useState([]);
  const [deletedCourses, setDeletedCourses] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courseChanges, setCourseChanges] = useState({}); // Track section changes
  const [courseFiles, setCourseFiles] = useState({}); // Track uploaded files for each course
  const [courseColors, setCourseColors] = useState({}); // Track color changes
  const [courseLocations, setCourseLocations] = useState({}); // Track location changes
  const [courseNames, setCourseNames] = useState({}); // Track name changes
  const [expandedCard, setExpandedCard] = useState(null); // Track which card is expanded
  const [courseDescriptions, setCourseDescriptions] = useState({});
  const [descriptionsLoading, setDescriptionsLoading] = useState({});
  const [isLoadingDescriptions, setIsLoadingDescriptions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
    primaryButtonText: "OK",
    secondaryButtonText: null,
    onPrimaryClick: null,
    onSecondaryClick: null,
  });

  // Tasks state
  const [todayTasks, setTodayTasks] = useState([]);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [tasksError, setTasksError] = useState(null);

  // Refs for task list containers
  const todayTasksListRef = useRef(null);
  const upcomingTasksListRef = useRef(null);

  // Course-specific tasks state
  const [courseTasksMap, setCourseTasksMap] = useState({});
  const [courseTasksLoading, setCourseTasksLoading] = useState({});

  // Pagination configuration
  const ITEMS_PER_PAGE = 6; // Increased for card layout
  const DESCRIPTION_CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds
  const totalPages = Math.ceil(courses.length / ITEMS_PER_PAGE) || 1;

  // Calculate paginated courses
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedCourses = courses.slice(startIndex, endIndex);

  // Fetch tasks from API; when showLoadingState=false runs silently in background
  const fetchTasks = async (showLoadingState = true) => {
    try {
      if (showLoadingState) setTasksLoading(true);
      setTasksError(null);

      const [todayResponse, upcomingResponse] = await Promise.all([
        apiClient.get('/tasks', { scope: 'today', status: 'open' }, getSessionToken),
        apiClient.get('/tasks', { scope: 'upcoming', status: 'open' }, getSessionToken)
      ]);

      if (todayResponse.status === 'success') {
        const tasks = todayResponse.data || [];
        setTodayTasks(tasks);
        localStorage.setItem(TASKS_CACHE_TODAY_KEY, JSON.stringify(tasks));
      }

      if (upcomingResponse.status === 'success') {
        const tasks = upcomingResponse.data || [];
        setUpcomingTasks(tasks);
        localStorage.setItem(TASKS_CACHE_UPCOMING_KEY, JSON.stringify(tasks));
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      if (showLoadingState) setTasksError('Failed to load tasks');
    } finally {
      if (showLoadingState) setTasksLoading(false);
    }
  };

  // Load tasks — show cache instantly, then refresh from API in background
  const loadTasks = () => {
    const cachedToday = localStorage.getItem(TASKS_CACHE_TODAY_KEY);
    const cachedUpcoming = localStorage.getItem(TASKS_CACHE_UPCOMING_KEY);

    if (cachedToday && cachedUpcoming) {
      try {
        setTodayTasks(JSON.parse(cachedToday));
        setUpcomingTasks(JSON.parse(cachedUpcoming));
        setTasksLoading(false);
        fetchTasks(false);
        return;
      } catch (e) {
        console.error('Error parsing cached tasks:', e);
      }
    }

    fetchTasks(true);
  };

  // Handle task status toggle
  const handleToggleTaskStatus = async (taskId, newStatus) => {
    try {
      const updateData = {
        status: newStatus
      };

      // If marking as done, set completed_at to now
      if (newStatus === 'done') {
        updateData.completed_at = new Date().toISOString();
      } else {
        // If marking as open, clear completed_at
        updateData.completed_at = null;
      }

      const response = await apiClient.patch(`/tasks/${taskId}`, updateData, getSessionToken);

      if (response.status === 'success') {
        const applyUpdate = (task) =>
          task.id === taskId
            ? { ...task, status: newStatus, completed_at: updateData.completed_at }
            : task;

        // Functional updaters so back-to-back toggles each apply to the latest
        // state instead of a snapshot captured before the await (which would let
        // a later toggle clobber an earlier one). Cache write happens inside so
        // localStorage stays consistent with the merged result.
        setTodayTasks(prev => {
          const next = prev.map(applyUpdate);
          localStorage.setItem(TASKS_CACHE_TODAY_KEY, JSON.stringify(next));
          return next;
        });
        setUpcomingTasks(prev => {
          const next = prev.map(applyUpdate);
          localStorage.setItem(TASKS_CACHE_UPCOMING_KEY, JSON.stringify(next));
          return next;
        });

        // Clear calendar cache so changes are reflected in calendar view
        localStorage.removeItem('ocean_calendar_events');
        localStorage.removeItem('ocean_calendar_ranges');
        localStorage.removeItem('ocean_calendar_timestamp');
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      throw error; // Re-throw to let TaskItem handle the error
    }
  };

  // Handle task updates (title, description, due_at)
  const handleUpdateTask = async (taskId, updatedData) => {
    try {
      const response = await apiClient.patch(
        `/tasks/${taskId}`,
        updatedData,
        getSessionToken
      );

      if (response.status === 'success') {
        const updatedTask = response.data;

        const updateTaskInList = (tasks) =>
          tasks.map(task => task.id === taskId ? { ...task, ...updatedTask } : task);

        // Functional updaters (see handleToggleTaskStatus) so concurrent edits
        // don't overwrite each other with a pre-await snapshot.
        setTodayTasks(prev => {
          const next = updateTaskInList(prev);
          localStorage.setItem(TASKS_CACHE_TODAY_KEY, JSON.stringify(next));
          return next;
        });
        setUpcomingTasks(prev => {
          const next = updateTaskInList(prev);
          localStorage.setItem(TASKS_CACHE_UPCOMING_KEY, JSON.stringify(next));
          return next;
        });

        // Update course-specific tasks if they exist
        if (updatedTask.course_id && courseTasksMap[updatedTask.course_id]) {
          setCourseTasksMap(prev => ({
            ...prev,
            [updatedTask.course_id]: updateTaskInList(prev[updatedTask.course_id])
          }));
        }

        console.log('Task updated successfully:', updatedTask);
      }
    } catch (error) {
      console.error('Error updating task:', error);
      throw error; // Re-throw to let TaskItem handle the error
    }
  };

  // Get course color by course ID
  const getCourseColor = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return courseColors[courseId] || course?.color || '#039be5';
  };

  // Calculate max-height for task lists to show 2 tasks by default
  // Uses ResizeObserver to dynamically adjust when tasks expand/collapse
  useEffect(() => {
    let rafId = null;

    const calculateMaxHeight = (ref) => {
      if (!ref.current) return;

      const taskItems = ref.current.children;

      if (taskItems.length >= 2) {
        const firstTaskHeight = taskItems[0].offsetHeight;
        const secondTaskHeight = taskItems[1].offsetHeight;
        const gap = 8; // Match CSS gap value
        const padding = 8; // Top and bottom padding
        const maxHeight = firstTaskHeight + secondTaskHeight + gap + padding;
        ref.current.style.maxHeight = `${maxHeight}px`;
      } else if (taskItems.length === 1) {
        // If only 1 task, set max-height to that task's height
        const taskHeight = taskItems[0].offsetHeight;
        const padding = 8;
        ref.current.style.maxHeight = `${taskHeight + padding}px`;
      }
    };

    // Initial calculation for both task lists
    calculateMaxHeight(todayTasksListRef);
    calculateMaxHeight(upcomingTasksListRef);

    // Use requestAnimationFrame to batch DOM reads/writes and prevent loops
    const rafCalculate = () => {
      // Cancel any pending animation frame
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      // Schedule calculation for next frame
      rafId = requestAnimationFrame(() => {
        calculateMaxHeight(todayTasksListRef);
        calculateMaxHeight(upcomingTasksListRef);
        rafId = null;
      });
    };

    // Create ResizeObserver to watch for task expansion/collapse
    const resizeObserver = new ResizeObserver(rafCalculate);

    // Observe all child task items in both lists
    if (todayTasksListRef.current) {
      Array.from(todayTasksListRef.current.children).forEach(child => {
        resizeObserver.observe(child);
      });
    }

    if (upcomingTasksListRef.current) {
      Array.from(upcomingTasksListRef.current.children).forEach(child => {
        resizeObserver.observe(child);
      });
    }

    // Cleanup observer on unmount or when dependencies change
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
    };
  }, [todayTasks, upcomingTasks]);

  // Fetch tasks for a specific course
  const fetchCourseTasks = async (courseId) => {
    try {
      setCourseTasksLoading(prev => ({ ...prev, [courseId]: true }));

      const response = await apiClient.get(
        `/courses/${courseId}/tasks`,
        { range: 'all' }, // Removed status and limit filters to get all tasks
        getSessionToken
      );

      if (response.status === 'success') {
        setCourseTasksMap(prev => ({
          ...prev,
          [courseId]: response.data || []
        }));
      }
    } catch (error) {
      console.error(`Error fetching tasks for course ${courseId}:`, error);
      setCourseTasksMap(prev => ({
        ...prev,
        [courseId]: []
      }));
    } finally {
      setCourseTasksLoading(prev => ({ ...prev, [courseId]: false }));
    }
  };

  // Handle task click (placeholder for future functionality)
  const handleTaskClick = (task) => {
    console.log('Task clicked:', task);
    // TODO: Open task detail modal or navigate to task page
  };

  // Handle task toggle (mark as complete/incomplete)
  const handleTaskToggle = async (task) => {
    const newStatus = task.status === 'done' ? 'open' : 'done';

    try {
      const updateData = {
        status: newStatus
      };

      // If marking as done, set completed_at to now
      if (newStatus === 'done') {
        updateData.completed_at = new Date().toISOString();
      } else {
        // If marking as open, clear completed_at
        updateData.completed_at = null;
      }

      const response = await apiClient.patch(`/tasks/${task.id}`, updateData, getSessionToken);

      if (response.status === 'success') {
        // Update the local task state without refetching
        if (task.course_id) {
          setCourseTasksMap(prev => {
            const courseTasks = prev[task.course_id] || [];
            const updatedTasks = courseTasks.map(t =>
              t.id === task.id
                ? { ...t, status: newStatus, completed_at: updateData.completed_at }
                : t
            );
            return {
              ...prev,
              [task.course_id]: updatedTasks
            };
          });
        }
      }
    } catch (error) {
      console.error('Error toggling task status:', error);
      throw error;
    }
  };

  const fetchDescriptions = async (courseList) => {
    if (isLoadingDescriptions) {
      console.log("Description fetch already in progress, skipping");
      return;
  }

    const cacheKey = `course_descriptions_${user?.uid}`;
    const cached = localStorage.getItem(cacheKey);

    const now = Date.now();
    let cachedDescriptions = {};
    let coursesToFetch = [];

    if (cached) {
      try {
        const cacheData = JSON.parse(cached);

        courseList.forEach(course => {
          const courseCache = cacheData[course.id];

          if (courseCache) {
            const isExpired = (now - courseCache.timestamp) > DESCRIPTION_CACHE_DURATION;

            if (!isExpired) {
              cachedDescriptions[course.id] = courseCache.description;
            } else {
              coursesToFetch.push(course);
            }
          } else {
            coursesToFetch.push(course);
          }
        });

        console.log(`Using cached descriptions for ${Object.keys(cachedDescriptions).length} courses, fetching ${coursesToFetch.length} courses`);
      } catch (error) {
        console.error("Error parsing cache:", error);
        coursesToFetch = courseList;
      }
    } else {
      coursesToFetch = courseList;
    }

    setCourseDescriptions(cachedDescriptions);

    const clearedLoading = {};
    Object.keys(cachedDescriptions).forEach(id => {
      clearedLoading[id] = false;
    });
    setDescriptionsLoading(clearedLoading);

    if (coursesToFetch.length === 0) {
      console.log("All courses have valid cached descriptions");
      return;
    }

    try {
      setIsLoadingDescriptions(true);
      const loadingState = { ...clearedLoading };
      coursesToFetch.forEach(course => {
        loadingState[course.id] = true;
      });
      setDescriptionsLoading(loadingState);

      const courseIds = coursesToFetch.map(c => c.id);

      const response = await apiClient.post(
        "/api/course-descriptions/bulk",
        { course_ids: courseIds },
        getSessionToken
      );

      if (response.status === "success" && response.data) {
        console.log("Fetched course descriptions:", response.data.descriptions);
        const descriptions = response.data.descriptions || {};

        const newDescriptions = { ...cachedDescriptions };
        const newLoadingState = {};
        const cacheData = cached ? JSON.parse(cached) : {};

        courseIds.forEach(id => {
          const description = descriptions[id];
          if (description) {
            newDescriptions[id] = description;
            cacheData[id] = {
              description: description,
              timestamp: now
            };
          } else {
            newDescriptions[id] = "No summary available for today";
            console.warn(`No description found for course ID ${id}`);
          }
          newLoadingState[id] = false;
        });

        localStorage.setItem(cacheKey, JSON.stringify(cacheData));

        setCourseDescriptions(newDescriptions);
        setDescriptionsLoading(newLoadingState);
      }

        else {
        throw new Error(response.message || "Failed to fetch course descriptions");
      }

    } catch (error) {
      console.error("Error fetching descriptions:", error);
      const fallbackDescriptions = { ...cachedDescriptions };
      const fallbackLoading = {};
      coursesToFetch.forEach(course => {
        fallbackDescriptions[course.id] = "Unable to load today's summary";
        fallbackLoading[course.id] = false;
      });
      setCourseDescriptions(fallbackDescriptions);
      setDescriptionsLoading(fallbackLoading);
    }
    finally {
      setIsLoadingDescriptions(false);
    }
  };
  
  // Initialize course state from data (used by both cache and API paths)
  const initializeCourseState = (courseData) => {
    setCourses(courseData);
    setOriginalCourses(JSON.parse(JSON.stringify(courseData))); // Deep copy for comparison

    // Initialize selected sections with current selections
    const initialSections = {};
    const initialColors = {};
    const initialLocations = {};
    const initialNames = {};
    courseData.forEach((course) => {
      if (course.selected_section) {
        initialSections[course.id] = course.selected_section;
      }
      if (course.color) {
        initialColors[course.id] = course.color;
      }
      initialLocations[course.id] = course.selected_section?.location || course.location || "";
      initialNames[course.id] = course.course_name;
    });
    setSelectedSections(initialSections);
    setCourseColors(initialColors);
    setCourseNames(initialNames);
    setCourseLocations(initialLocations);

    // Reset deleted courses, changes, and files
    setDeletedCourses(new Set());
    setCourseChanges({});
    setCourseFiles({});
    setExpandedCard(null);
  };

  // Fetch courses from API (can run in background without showing loading)
  const fetchCoursesFromAPI = async (showLoadingState = true) => {
    console.log('[COURSES] fetchCoursesFromAPI called, showLoadingState:', showLoadingState);
    try {
      if (showLoadingState) {
        setLoading(true);
      }
      setError(null);

      const response = await apiClient.get("/courses", {}, getSessionToken);

      const result = response;
      console.log('[COURSES] API response:', result);
      if (result.status === "success" && result.data) {
        console.log("[COURSES] Raw courses data from backend:", result.data);
        console.log("[COURSES] Number of courses:", result.data.length);

        // Save courses to localStorage for use by other components (e.g., calendar)
        localStorage.setItem("courses", JSON.stringify(result.data));

        initializeCourseState(result.data);
        fetchDescriptions(result.data);
      } else {
        throw new Error(result.error || "Failed to fetch courses");
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
      if (showLoadingState) {
        setError("Failed to load courses. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Load courses - check cache first, then refresh from API in background
  const loadCourses = async () => {
    // Try to load from localStorage cache first
    const cachedCourses = localStorage.getItem("courses");

    if (cachedCourses) {
      try {
        const courseData = JSON.parse(cachedCourses);
        if (courseData && courseData.length > 0) {
          // Show cached data immediately (no loading screen)
          initializeCourseState(courseData);
          fetchDescriptions(courseData);
          setLoading(false);

          // Refresh from API in background
          fetchCoursesFromAPI(false);
          return;
        }
      } catch (e) {
        console.error("Error parsing cached courses:", e);
      }
    }

    // No cache or invalid cache - fetch from API with loading state
    await fetchCoursesFromAPI(true);
  };

  // Load courses from cache/backend on mount
  // getSessionToken from Clerk is not memoized, so including loadCourses/fetchTasks
  // in deps would cause infinite loop. Effect intentionally runs only on initDone/user.uid changes.
  // TODO: Fix root cause by wrapping getSessionToken in useCallback in use-clerk-auth.js
  useEffect(() => {
    if (initDone && user?.uid) {
      loadCourses();
      loadTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initDone, user?.uid]);

  // Reset to page 1 when courses change
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [courses.length, currentPage, totalPages]);

  const handleSectionChange = (courseId, selectedSection) => {
    // Update selected section
    setSelectedSections((prev) => ({
      ...prev,
      [courseId]: selectedSection,
    }));

    // Check if this is the same as the original section
    const originalCourse = originalCourses.find(c => c.id === courseId);
    const originalSection = originalCourse?.selected_section;
    const isBackToOriginal = originalSection &&
      selectedSection.name === originalSection.name &&
      selectedSection.days_of_week === originalSection.days_of_week &&
      selectedSection.timing === originalSection.timing;

    // Track changes for API payload (or remove if back to original)
    setCourseChanges((prev) => {
      if (isBackToOriginal) {
        // Remove from changes if back to original
        const { [courseId]: _, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [courseId]: selectedSection,
      };
    });
  };

  const handleCourseNameChange = (courseId, newName) => {
    setCourseNames((prev) => ({
      ...prev,
      [courseId]: newName,
    }));

    // Track this as a change
    setCourseChanges((prev) => ({
      ...prev,
      [courseId]: selectedSections[courseId] || courses.find(c => c.id === courseId)?.selected_section,
    }));
  };

  const handleColorChange = (courseId, color) => {
    setCourseColors((prev) => ({
      ...prev,
      [courseId]: color,
    }));

    // Check if this is the same as the original color
    const originalCourse = originalCourses.find(c => c.id === courseId);
    const isBackToOriginal = originalCourse && color === originalCourse.color;

    // Track changes for API payload (or remove if back to original)
    setCourseChanges((prev) => {
      if (isBackToOriginal) {
        const { [courseId]: _, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [courseId]: selectedSections[courseId] || courses.find(c => c.id === courseId)?.selected_section,
      };
    });
  };

  const handleLocationChange = (courseId, newLocation) => {
    setCourseLocations((prev) => ({
    ...prev,
    [courseId]: newLocation,
  }));

  // Track this as a change
  setCourseChanges((prev) => ({
    ...prev,
    [courseId]: selectedSections[courseId] || courses.find(c => c.id === courseId)?.selected_section,
  }));
};

  const handleCardClick = (courseId) => {
    const isExpanding = expandedCard !== courseId;
    setExpandedCard(expandedCard === courseId ? null : courseId);

    // Fetch course tasks when expanding a card
    if (isExpanding && !courseTasksMap[courseId]) {
      fetchCourseTasks(courseId);
    }
  };

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  // Handle success/error modal from navigation state (after patch job completes)
  useEffect(() => {
    console.log('[COURSES] useEffect triggered, location.state:', location.state);
    if (location.state?.showSuccessModal) {
      console.log('[COURSES] showSuccessModal detected, clearing caches and refetching...');
      // Clear calendar caches to reflect deleted courses/events
      localStorage.removeItem('ocean_calendar_events');
      localStorage.removeItem('ocean_calendar_ranges');
      localStorage.removeItem('ocean_calendar_timestamp');

      // Clear courses cache and refresh from API in background
      localStorage.removeItem('courses');
      console.log('[COURSES] Calling fetchCoursesFromAPI...');
      fetchCoursesFromAPI(false);

      setModalState({
        isOpen: true,
        type: "success",
        title: "Changes saved successfully",
        message: "Your calendar has been updated.",
        primaryButtonText: "OK",
        onPrimaryClick: closeModal,
      });
      // Clear the state so modal doesn't show again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.state?.showErrorModal) {
      // Clear calendar caches even on error (partial changes may have occurred)
      localStorage.removeItem('ocean_calendar_events');
      localStorage.removeItem('ocean_calendar_ranges');
      localStorage.removeItem('ocean_calendar_timestamp');

      setModalState({
        isOpen: true,
        type: "error",
        title: "Error saving changes",
        message: location.state.errorMessage || "Failed to save changes. Please try again.",
        primaryButtonText: "OK",
        onPrimaryClick: closeModal,
      });
      // Clear the state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const handleFileUpload = (courseId, file) => {
    if (file) {
      setCourseFiles((prev) => ({
        ...prev,
        [courseId]: file,
      }));
    }
  };

  const handleUpdate = (courseId) => {
    // Create a hidden file input
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".pdf,.docx";
    fileInput.style.display = "none";

    fileInput.onchange = (event) => {
      const file = event.target.files[0];
      if (file) {
        handleFileUpload(courseId, file);

        const course = courses.find((c) => c.id === courseId);
        setModalState({
          isOpen: true,
          type: "success",
          title: "File uploaded successfully",
          message: `File "${file.name}" has been attached to ${courseNames[courseId] || course.course_name}. Click "Save & push to calendar" to apply changes.`,
          primaryButtonText: "OK",
          onPrimaryClick: closeModal,
        });
      }
      document.body.removeChild(fileInput);
    };

    fileInput.oncancel = () => {
      document.body.removeChild(fileInput);
    };

    document.body.appendChild(fileInput);
    fileInput.click();
  };

  const handleDelete = (courseId) => {
    setDeletedCourses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(courseId)) {
        // If already deleted, restore it
        newSet.delete(courseId);
      } else {
        // Mark as deleted
        newSet.add(courseId);
      }
      return newSet;
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleUndoEdits = () => {
    // Reset all changes
    setCourses(JSON.parse(JSON.stringify(originalCourses)));
    setCourseChanges({});
    setDeletedCourses(new Set());
    setCourseFiles({});

    // Reset selected sections to original
    const originalSections = {};
    const originalColors = {};
    const originalLocations = {};
    const originalNames = {};
    originalCourses.forEach((course) => {
      if (course.selected_section) {
        originalSections[course.id] = course.selected_section;
      }
      if (course.color) {
        originalColors[course.id] = course.color;
      }
      originalLocations[course.id] = course.selected_section?.location || course.location || "";
      originalNames[course.id] = course.course_name;
    });
    setSelectedSections(originalSections);
    setCourseColors(originalColors);
    setCourseNames(originalNames);
    setCourseLocations(originalLocations);
    setExpandedCard(null);
  };

  const handleSaveAndPush = async () => {
    // Check if there are any changes to process
    const hasUpdates =
      Object.keys(courseChanges).length > 0 ||
      Object.keys(courseFiles).length > 0;
    const hasDeletes = deletedCourses.size > 0;

    if (!hasUpdates && !hasDeletes) {
      setModalState({
        isOpen: true,
        type: "info",
        title: "No changes to save",
        message: "Please make some changes before saving.",
        primaryButtonText: "OK",
        onPrimaryClick: closeModal,
      });
      return;
    }

    setIsSaving(true);
    try {
      // Prepare updates
      const updates = [];
      const processedCourses = new Set();

      // Process section changes and file uploads
      [...Object.keys(courseChanges), ...Object.keys(courseFiles)].forEach(
        (courseId) => {
          if (
            processedCourses.has(courseId) ||
            deletedCourses.has(parseInt(courseId))
          ) {
            return; // Skip if already processed or marked for deletion
          }

          const course = courses.find((c) => c.id === parseInt(courseId));
          if (!course) return;

          const selectedSection =
            selectedSections[courseId] || course.selected_section;
          const hasFileUploaded = courseFiles[courseId] !== undefined;

          const originalCourse = originalCourses.find(c => c.id === parseInt(courseId));
          const is_metadata_only = (
            !hasFileUploaded &&
            selectedSection?.name === originalCourse?.selected_section?.name &&
            selectedSection?.days_of_week === originalCourse?.selected_section?.days_of_week &&
            selectedSection?.timing === originalCourse?.selected_section?.timing
          );

          const updateItem = {
            id: course.id,
            calendar_type: course.calendar_type,
            course_name: courseNames[courseId] || course.course_name,
            selected_section: {
              ...selectedSection,
              location: courseLocations[courseId] !== undefined ? courseLocations[courseId] : selectedSection?.location
            },
            has_file: hasFileUploaded,
            color: courseColors[courseId] || course.color,
            is_metadata_only: is_metadata_only,
          };

          updates.push(updateItem);
          processedCourses.add(courseId);
        }
      );

      // Prepare deletions
      const deletions = [];
      deletedCourses.forEach((courseId) => {
        const course = courses.find((c) => c.id === courseId);
        if (course) {
          deletions.push({
            id: course.id,
            calendar_type: course.calendar_type,
            course_name: course.course_name,
          });
        }
      });

      // Create FormData
      const formData = new FormData();

      // Add payload as JSON string (without file objects)
      const payload = {
        update: updates,
        delete: deletions,
      };
      formData.append("payload", JSON.stringify(payload));

      // Add files with course-specific field names
      Object.keys(courseFiles).forEach((courseId) => {
        if (!deletedCourses.has(parseInt(courseId))) {
          const course = courses.find((c) => c.id === parseInt(courseId));
          if (course && courseFiles[courseId]) {
            const fieldName = `file_${courseNames[courseId] || course.course_name}`;
            formData.append(fieldName, courseFiles[courseId]);
          }
        }
      });

      // Create job (returns immediately)
      const token = await getSessionToken();
      const response = await fetch(`${config.API_BASE_URL}/patch_courses_streaming`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status !== "success") {
        throw new Error(result.message || "Failed to create job");
      }

      const jobId = result.data.job_id;
      const initialMessage = result.data.progress_message;

      // Navigate to streaming loading screen
      navigate("/streaming-loading", {
        state: {
          jobId,
          initialMessage,
          isPatchJob: true
        }
      });

    } catch (error) {
      console.error("Error saving changes:", error);

      setModalState({
        isOpen: true,
        type: "error",
        title: "Error saving changes",
        message: error.message || "Failed to save changes. Please try again.",
        primaryButtonText: "OK",
        onPrimaryClick: closeModal,
      });
      setIsSaving(false);
    }
  };

  // Check if any section changes require a file upload
  const hasSectionChangeWithoutFile = () => {
    return Object.keys(courseChanges).some((courseId) => {
      if (deletedCourses.has(parseInt(courseId))) return false;

      const originalCourse = originalCourses.find(c => c.id === parseInt(courseId));
      const selectedSection = selectedSections[courseId];
      const hasFileUploaded = courseFiles[courseId] !== undefined;

      const sectionChanged = (
        selectedSection?.name !== originalCourse?.selected_section?.name ||
        selectedSection?.days_of_week !== originalCourse?.selected_section?.days_of_week ||
        selectedSection?.timing !== originalCourse?.selected_section?.timing
      );

      return sectionChanged && !hasFileUploaded;
    });
  };

  // Check if Save & Push button should be enabled
  const isSaveAndPushEnabled =
    (deletedCourses.size > 0 ||
    Object.keys(courseChanges).length > 0 ||
    Object.keys(courseFiles).length > 0) &&
    !hasSectionChangeWithoutFile();

  // Show loading state
  if (loading) {
    const isSaving = Object.keys(courseChanges).length > 0 || 
                     Object.keys(courseFiles).length > 0 || 
                     deletedCourses.size > 0;
    
    return (
      <Loading 
        text={isSaving ? "Updating your calendar" : "Loading courses"}
        description={isSaving ? "This may take a few moments while we process your changes" : "Please wait..."}
      />
    );
  }

  // Show error state
  if (error) {
    return (
      <div className={s.coursesPageContainer}>
        <div className={s.container}>
          <PageHeader title="Error" description={error} />
        </div>
      </div>
    );
  }

  // Generate smart description based on current date
  const getSmartDescription = () => {
    const today = new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    return today;
  };

  return (
    <div className={s.coursesPageContainer}>
      <div className={s.container}>
        <div className={s.headerRow}>
          <PageHeader title="Courses" />
          <button
            className={s.addCoursesButton}
            onClick={() => navigate('/upload')}
          >
            <span className={s.buttonText}>Add courses </span>
            <span className={s.buttonIcon}>+</span>
          </button>
        </div>
        <p className={s.pageSubtitle}>{getSmartDescription()}</p>

        {/* Tasks Widget */}
        {courses.length > 0 && (
          <div className={s.tasksWidget} data-tour="tasks-section">
            <div className={s.tasksHeader}>
              <h2>This week's tasks</h2>
            </div>

            {tasksLoading ? (
              <div className={s.tasksLoading}>Loading tasks...</div>
            ) : tasksError ? (
              <div className={s.tasksError}>{tasksError}</div>
            ) : (
              <div className={s.tasksContent}>
                {/* Today Section */}
                <div className={s.tasksSection}>
                  <h3 className={s.sectionTitle}>TODAY</h3>
                  {todayTasks.length === 0 ? (
                    <div className={s.emptyState}>
                      <p className={s.emptyText}>No tasks</p>
                    </div>
                  ) : (
                    <div className={s.tasksList} ref={todayTasksListRef}>
                      {todayTasks.map(task => (
                        <TaskItem
                          key={task.id}
                          task={task}
                          onToggleStatus={handleToggleTaskStatus}
                          onSave={handleUpdateTask}
                          courseColor={getCourseColor(task.course_id)}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Upcoming Section */}
                <div className={s.tasksSection}>
                  <h3 className={s.sectionTitle}>UPCOMING</h3>
                  {upcomingTasks.length === 0 ? (
                    <div className={s.emptyState}>
                      <p className={s.emptyText}>No tasks</p>
                    </div>
                  ) : (
                    <div className={s.tasksList} ref={upcomingTasksListRef}>
                      {upcomingTasks.map(task => (
                        <TaskItem
                          key={task.id}
                          task={task}
                          onToggleStatus={handleToggleTaskStatus}
                          onSave={handleUpdateTask}
                          courseColor={getCourseColor(task.course_id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {courses.length === 0 ? (
          <div className={s.noCoursesContainer}>
            <p className={s.noCoursesMessage}>No courses available</p>
          </div>
        ) : (
          <>
            <div className={s.coursesGrid} data-tour="course-list">
              {paginatedCourses.map((course) => (
                <div 
                  key={course.id}
                  className={`${s.courseCard} ${deletedCourses.has(course.id) ? s.deleted : ''} ${expandedCard === course.id ? s.expanded : ''}`}
                  onClick={() => handleCardClick(course.id)}
                >
                  <CourseCard
                    courseData={{
                      ...course,
                      course_name: courseNames[course.id] || course.course_name,
                      color: courseColors[course.id] || course.color,
                    }}
                    selectedSection={selectedSections[course.id]}
                    originalSection={originalCourses.find(c => c.id === course.id)?.selected_section}
                    isExpanded={expandedCard === course.id}
                    isDeleted={deletedCourses.has(course.id)}
                    hasFile={!!courseFiles[course.id]}
                    onSectionChange={handleSectionChange}
                    onCourseNameChange={handleCourseNameChange}
                    onColorChange={handleColorChange}
                    onLocationChange={handleLocationChange}
                    onCardClick={handleCardClick}
                    onUpdate={handleUpdate}
                    onDelete={handleDelete}
                    description={courseDescriptions[course.id]}
                    isDescriptionLoading={descriptionsLoading[course.id]}
                    courseTasks={courseTasksMap[course.id] || []}
                    tasksLoading={courseTasksLoading[course.id] || false}
                    onTaskClick={handleTaskClick}
                    onTaskToggle={handleTaskToggle}
                    onTaskUpdate={handleUpdateTask}
                  />
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                handlePageChange={handlePageChange}
              />
            )}

            <div className={s.coursesBottomActions}>
              <Button
                variant="secondary"
                size="large"
                onClick={handleUndoEdits}
              >
                Undo edits
              </Button>
              <Button
                variant="primary"
                size="large"
                onClick={handleSaveAndPush}
                disabled={isSaving || !isSaveAndPushEnabled}
                loading={isSaving}
              >
                Save & push to calendar
              </Button>
            </div>
          </>
        )}

        <Modal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          title={modalState.title}
          message={modalState.message}
          type={modalState.type}
          primaryButtonText={modalState.primaryButtonText}
          secondaryButtonText={modalState.secondaryButtonText}
          onPrimaryClick={modalState.onPrimaryClick}
          onSecondaryClick={modalState.onSecondaryClick}
        />
      </div>
    </div>
  );
};

export default Courses;